import {
  useCountryAllWarehouseListQuery,
  useCountryShippingRateAddMutation,
  useCountryShippingRateUpdateMutation,
} from 'api';
import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { useWatch } from 'antd/es/form/Form';
import { ErrorMappingValueType } from 'typings';
import { getUpdatedPayload } from 'utils/helpers';
import { ShippingMethodType } from 'typings/client';
import { FormProps, notification } from 'components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IRatesAddEditModal } from './index';
import { Price, SpecialRate } from '..';

const keysToSkip: string[] = [
  'edit_measurementRange',
  'edit_metrics',
  'edit_price',
  'measurementRange',
  'metrics',
  'price',
];

export const useRatesAddEditModal = ({ dataToEdit, onModalStateChange, modalState }: IRatesAddEditModal) => {
  const [editId, setEditId] = useState<string | null>(null);
  const [prices, setPrices] = useState<Price[]>([]);
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);
  const [triggerValidation, setTriggerValidation] = useState(false);
  const [form] = Form.useForm();
  const metrics = useWatch('metrics', form);
  const measurementRange = useWatch('measurementRange', form);
  const price = useWatch('price', form);

  const { id } = useParams() as { id: string };
  const [notificationApi, contextHolder] = notification.useNotification();
  const { data: warehouseData = [] } = useCountryAllWarehouseListQuery();

  const [createShippingRate, { isLoading: createIsLoading }] = useCountryShippingRateAddMutation();
  const [updateShippingRate, { isLoading: updateIsLoading }] = useCountryShippingRateUpdateMutation();

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const isChanged = useMemo(() => {
    return [metrics, measurementRange, price].some((value) => value !== undefined && value !== '');
  }, [metrics, measurementRange, price]);

  const onPriceRowCancel = useCallback(() => setEditId(null), []);
  const onPriceRowEdit = useCallback(
    (measurementRange: string) => {
      setEditId(measurementRange);
      const priceRowData = prices.find((_price) => _price.measurementRange === measurementRange);
      if (priceRowData) {
        form.setFieldsValue({
          edit_metrics: priceRowData.metrics,
          edit_measurementRange: priceRowData.measurementRange,
          edit_price: priceRowData.price,
        });
      }
    },
    [prices, form]
  );

  const onPriceRowSave = useCallback(
    async (measurementRange: string) => {
      try {
        await form.validateFields(['edit_metrics', 'edit_measurementRange', 'edit_price']);
        const { edit_metrics, edit_measurementRange, edit_price } = form.getFieldsValue([
          'edit_metrics',
          'edit_measurementRange',
          'edit_price',
        ]);
        setPrices((prevState) =>
          prevState.map((price) => {
            if (price.measurementRange === measurementRange) {
              return {
                ...price,
                metrics: edit_metrics,
                measurementRange: edit_measurementRange,
                price: edit_price,
              };
            } else {
              return price;
            }
          })
        );

        form.resetFields(['edit_metrics', 'edit_measurementRange', 'edit_price']);
        setEditId(null);
      } catch {
        // handle catch
      }
    },
    [form]
  );
  const onPriceRowRemove = useCallback((measurementRange: string) => {
    setPrices((prevState) => prevState.filter((_price) => _price.measurementRange !== measurementRange));
    setDisableSaveBtn(false);
  }, []);

  const onPriceSave = useCallback(async () => {
    setTriggerValidation(true);
    try {
      await form.validateFields(['metrics', 'measurementRange', 'price']);
      const { metrics, measurementRange, price } = form.getFieldsValue(['metrics', 'measurementRange', 'price']);
      setPrices((prevState) => [
        ...prevState,
        {
          metrics,
          measurementRange,
          price,
        },
      ]);
      form.resetFields(['metrics', 'measurementRange', 'price']);
      setTriggerValidation(false);
    } catch {
      // handle catch
    }
  }, [form]);

  const onPriceCancel = useCallback(() => {
    form.resetFields(['metrics', 'measurementRange', 'price']);
    setTriggerValidation(false);
  }, [form]);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
    setPrices([]);
    setEditId(null);
    setDisableSaveBtn(true);
  }, [form, onModalStateChange]);

  const onFinish: FormProps<SpecialRate>['onFinish'] = useCallback(
    (values: SpecialRate) => {
      const payload = getUpdatedPayload(values, dataToEdit, keysToSkip);
      const priceItems = form.getFieldsValue(['metrics', 'measurementRange', 'price']);
      const isAllItems = Object.values(priceItems || {}).every((_item) => !!_item);

      // TODO need to clarify weather it is multiple
      payload.shippingMethods = values.shippingMethods ? [values.shippingMethods] : ([] as ShippingMethodType[] as any);
      payload.countryId = id;
      if (dataToEdit) {
        payload.id = dataToEdit.id;
      }
      payload.prices = prices;
      if (isAllItems) {
        payload.prices = [...payload.prices, priceItems];
      }
      (dataToEdit ? updateShippingRate(payload) : createShippingRate(payload))
        .then(({ data, error }: any) => {
          if (data?.success) {
            openNotificationWithIcon({
              type: 'success',
              message: `Shipping Rate was successfully ${dataToEdit ? 'updated' : 'created'}.`,
            });
            onCancel();
          } else if (!error.data.success) {
            const { data = {} } = error.data;
            Object.keys(data).forEach((_key: string) => {
              openNotificationWithIcon({
                type: 'error',
                message: data[_key],
              });
            });
          } else if (error.data.error) {
            openNotificationWithIcon({
              type: 'error',
              message: 'Something went wrong!',
            });
          }
        })
        .catch((err) => {
          console.log(err, 'error');
        });
    },
    [dataToEdit, createShippingRate, openNotificationWithIcon, id, updateShippingRate, prices, onCancel, form]
  );

  useEffect(() => {
    if (dataToEdit && modalState) {
      form.setFieldsValue({
        ...dataToEdit,
        warehouseId: dataToEdit.warehouse.id,
        shippingMethods: dataToEdit.shippingMethods?.length ? dataToEdit.shippingMethods[0] : '',
      });
    }
    if (dataToEdit?.prices && modalState) {
      setPrices(dataToEdit.prices);
    }
  }, [dataToEdit, form, modalState]);

  return {
    onPriceRowRemove,
    onPriceRowCancel,
    onPriceRowSave,
    onPriceRowEdit,
    onPriceSave,
    onCancel,
    onFinish,
    form,
    contextHolder,
    editId,
    triggerValidation,
    prices,
    isChanged,
    onPriceCancel,
    isLoading: createIsLoading || updateIsLoading,
    disableSaveBtn,
    warehouseData,
  };
};
